import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// Trade Packages Components
import { AuthProvider } from './contexts/AuthContext';
import { NavigationProvider } from './contexts/NavigationContext';
import PackageView from './components/TradePackages/PackageView';
import TradeBuilderHome from './components/TradePackages/TradeBuilderHome';
import TradePackagesLogin from './components/TradePackages/TradePackagesLogin';
import VerifyMagicLink from './components/VerifyMagicLink';
import TradeCanvas from './components/TradeCanvas/ScoutCanvas';
import ManagerIntelProfile from './components/ManagerIntel/ManagerIntelProfile';
import IntelHome from './components/ManagerIntel/IntelHome';
import SimulateHome from './components/MonteCarlo/SimulateHome';
import MonteCarloSimulator from './components/MonteCarlo/MonteCarloSimulator';
import DesignShowcase from './components/MonteCarlo/DesignShowcase';
import SeasonReplay from './components/MonteCarlo/SeasonReplay';
import OrphanEdit from './components/OrphanMarketplace/OrphanEdit';
import StartupEdit from './components/StartupMarketplace/StartupEdit';
import MarketplacesHome from './components/Marketplaces/MarketplacesHome';
import ListingDetail from './components/Marketplaces/ListingDetail';
import ListTeamWizard from './components/Marketplaces/ListTeamWizard';
import ApplicationsConsole from './components/Marketplaces/ApplicationsConsole';
import RateMyTradeHome from './components/RateMyTrade/RateMyTradeHome';
import RateMyTradeView from './components/RateMyTrade/RateMyTradeView';
import RateMyTradeCreate from './components/RateMyTrade/RateMyTradeCreate';
import CommandCenter from './components/Admin/CommandCenter';
import AnalystLanding from './components/AnalystReview/AnalystLanding';
import AnalystDashboard from './components/AnalystReview/AnalystDashboard';
import AnalystReviewDetail from './components/AnalystReview/AnalystReviewDetail';
import AnalystReviewResult from './components/AnalystReview/AnalystReviewResult';
import AnalyticsWrapper from './components/AnalyticsWrapper';
import ShellLayout from './shell/ShellLayout';
import ShellHome from './shell/Home';
import TradeArenaQueue from './components/TradeArena/TradeArenaQueue';
import TradeArenaRoom from './components/TradeArena/TradeArenaRoom';
import TradeArenaEnd from './components/TradeArena/TradeArenaEnd';
import TradeArenaInvite from './components/TradeArena/TradeArenaInvite';
import CurveToday from './components/Curve/CurveToday';
import MarketPortfolio from './components/Market/MarketPortfolio';
import MarketBrowse from './components/Market/MarketBrowse';
import MarketAsset from './components/Market/MarketAsset';
import MarketPickAsset from './components/Market/MarketPickAsset';
import MarketLeaderboard from './components/Market/MarketLeaderboard';
import CurveSchedule from './components/Curve/CurveSchedule';

// Component to handle legacy hash-based URLs (e.g., /#/dashboard -> /dashboard)
const HashRedirect = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#/')) {
      const cleanPath = hash.substring(1);
      navigate(cleanPath, { replace: true });
    }
  }, [navigate]);

  return null;
};

// Param-preserving redirect for legacy detail-scoped routes
const RedirectWithId = ({ to }) => {
  const { id } = useParams();
  return <Navigate to={to.replace(':id', id)} replace />;
};

const App = () => {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <NavigationProvider>
            <AnalyticsWrapper>
              <HashRedirect />
              <Routes>
              {/* Authentication — inside the shell like everything else */}
              <Route element={<ShellLayout requireAuth={false} />}>
                <Route path="/login" element={<TradePackagesLogin />} />
                <Route path="/verify/:token" element={<VerifyMagicLink />} />
              </Route>

              {/* Unified Marketplaces — in the shell; browse + detail are public */}
              <Route element={<ShellLayout requireAuth={false} />}>
                <Route path="/marketplaces" element={<MarketplacesHome />} />
                <Route path="/orphan-marketplace/:id" element={<ListingDetail type="orphan" />} />
                <Route path="/startup-marketplace/:id" element={<ListingDetail type="startup" />} />
              </Route>
              <Route element={<ShellLayout />}>
                <Route path="/orphan-marketplace/list" element={<ListTeamWizard type="orphan" />} />
                <Route path="/startup-marketplace/list" element={<ListTeamWizard type="startup" />} />
                <Route path="/orphan-marketplace/:id/applications" element={<ApplicationsConsole type="orphan" />} />
                <Route path="/startup-marketplace/:id/applications" element={<ApplicationsConsole type="startup" />} />
                <Route path="/orphan-marketplace/:id/edit" element={<OrphanEdit />} />
                <Route path="/startup-marketplace/:id/edit" element={<StartupEdit />} />
              </Route>

              {/* Legacy dashboard — superseded by the shell Home */}
              <Route path="/dashboard" element={<Navigate to="/home" replace />} />

              {/* The shell Home — public; anonymous visitors get the in-world landing */}
              <Route element={<ShellLayout requireAuth={false} />}>
                <Route path="/home" element={<ShellHome />} />
              </Route>

              {/* Orphan Marketplace Routes (Primary) */}
              {/* The shell IS the site — everyone lands on Home */}
              <Route path="/" element={<Navigate to="/home" replace />} />
              <Route path="/orphan-marketplace" element={<Navigate to="/marketplaces" replace />} />
              {/* /orphan-marketplace/list and :id/edit render in the shell (routes above) */}
              {/* apply now lives inline on the shell detail page */}
              <Route path="/orphan-marketplace/:id/apply" element={<RedirectWithId to="/orphan-marketplace/:id" />} />
              {/* :id/applications now renders the shell ApplicationsConsole (route above) */}
              {/* /orphan-marketplace/:id now renders the shell ListingDetail (route above) */}

              {/* Startup Marketplace Routes */}
              <Route path="/startup-marketplace" element={<Navigate to="/marketplaces?type=startup" replace />} />
              {/* /startup-marketplace/list and :id/edit render in the shell (routes above) */}
              <Route path="/startup-marketplace/:id/apply" element={<RedirectWithId to="/startup-marketplace/:id" />} />
              {/* :id/applications now renders the shell ApplicationsConsole (route above) */}
              {/* /startup-marketplace/:id now renders the shell ListingDetail (route above) */}

              {/* Rate My Trade — in the shell; feed + detail public (anonymous voting) */}
              <Route element={<ShellLayout requireAuth={false} />}>
                <Route path="/rate-my-trade" element={<RateMyTradeHome />} />
                <Route path="/rate-my-trade/:shareId" element={<RateMyTradeView />} />
              </Route>
              <Route element={<ShellLayout />}>
                <Route path="/rate-my-trade/create" element={<RateMyTradeCreate />} />
              </Route>

              {/* Manager Intel — landing + relit profile, both in the shell (public) */}
              <Route element={<ShellLayout requireAuth={false} />}>
                <Route path="/intel" element={<IntelHome />} />
                <Route path="/manager-intel/:sleeperUsername" element={<ManagerIntelProfile />} />
              </Route>

              {/* Monte Carlo Simulation — in the shell, public (shared sim links work signed out) */}
              <Route element={<ShellLayout requireAuth={false} />}>
                <Route path="/simulate" element={<SimulateHome />} />
                <Route path="/simulate/:leagueId" element={<MonteCarloSimulator />} />
              </Route>
              <Route path="/simulate/design" element={<DesignShowcase />} />
              <Route path="/simulate/replay" element={<SeasonReplay />} />

              {/* The Taco Stand — live 1v1 negotiation game. Queue + room require auth; result page is public */}
              <Route element={<ShellLayout requireAuth={false} />}>
                <Route path="/taco-stand" element={<TradeArenaQueue />} />
                <Route path="/taco-stand/invite/:code" element={<TradeArenaInvite />} />
                <Route path="/taco-stand/room/:sessionId" element={<TradeArenaRoom />} />
                <Route path="/taco-stand/result/:sessionId" element={<TradeArenaEnd />} />
              </Route>

              {/* The Curve — daily draw-the-value-line game (dark launch, public + guest draws) */}
              <Route element={<ShellLayout requireAuth={false} />}>
                <Route path="/curve" element={<CurveToday />} />
                <Route path="/curve/schedule" element={<CurveSchedule />} />
                <Route path="/curve/tape" element={<Navigate to="/curve" replace />} />
              </Route>

              {/* The Market — paper trading on KTC values (public: guest trades until the gate) */}
              <Route element={<ShellLayout requireAuth={false} />}>
                <Route path="/market" element={<MarketPortfolio />} />
                <Route path="/market/browse" element={<MarketBrowse />} />
                <Route path="/market/p/:playerId" element={<MarketAsset />} />
                <Route path="/market/pick/:pickLabel" element={<MarketPickAsset />} />
                <Route path="/market/leaderboard" element={<MarketLeaderboard />} />
              </Route>

              {/* Trade Builder — in-shell home (public: anonymous username flow still works) */}
              <Route element={<ShellLayout requireAuth={false} />}>
                <Route path="/trade-packages" element={<TradeBuilderHome />} />
              </Route>
              {/* Canvas — relit, in the shell (public: anonymous build + magic-link send) */}
              <Route element={<ShellLayout requireAuth={false} />}>
                <Route path="/trade-packages/canvas/:username/:league_id/:recipient_id" element={<TradeCanvas />} />
              </Route>
              {/* Legacy flow steps — superseded by the in-shell Trade Builder flow */}
              <Route path="/trade-packages/leagues/:username" element={<Navigate to="/trade-packages" replace />} />
              <Route path="/trade-packages/marketplace/:username/:league_id" element={<Navigate to="/trade-packages" replace />} />
              <Route path="/trade-packages/dashboard" element={<Navigate to="/trade-packages" replace />} />
              <Route path="/trade-packages/login" element={<Navigate to="/login" replace />} />
              {/* Public package view — PATH IMMUTABLE (emails + shares); now inside the shell */}
              <Route element={<ShellLayout requireAuth={false} />}>
                <Route path="/trade-packages/:unique_link" element={<PackageView />} />
              </Route>

              {/* Command Center - Only accessible to admins */}
              <Route path="/admin" element={<CommandCenter />} />

              {/* Analyst profiles — public hub at /analyst/:slug (e.g. /analyst/fantasynerdboi).
                  Fixed analyst-tool routes come first so they win over the :slug wildcard.
                  /review/:slug kept as a redirect for any existing links. */}
              <Route element={<ShellLayout />}>
                <Route path="/analyst/dashboard" element={<AnalystDashboard />} />
                <Route path="/analyst/reviews/:reviewId" element={<AnalystReviewDetail />} />
              </Route>
              <Route element={<ShellLayout requireAuth={false} />}>
                <Route path="/analyst/:slug" element={<AnalystLanding />} />
                <Route path="/analyst/:slug/result/:reviewId" element={<AnalystReviewResult />} />
                {/* Legacy /review/:slug — redirect to the new canonical URL */}
                <Route path="/review/:slug" element={<AnalystLanding />} />
                <Route path="/review/:slug/result/:reviewId" element={<AnalystReviewResult />} />
              </Route>

              {/* Catch-all — anything not matched above redirects home */}
              <Route path="/*" element={<Navigate to="/" replace />} />
            </Routes>
            </AnalyticsWrapper>
          </NavigationProvider>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
};

export default App;
